import React, { Suspense } from "react";
import { Helmet } from "react-helmet";
import { Container } from "../components";
import { PulseLoader } from "../components/pulse_loader";
import {
  CatalogOffering,
  PartnerCatalog,
  PartnerCatalogCategory,
} from "../data/catalog";
import { useMediaQuery, useQueryString } from "../hooks";
import { FooterV2, OfferingHeader } from "../offering_v2/components";
import {
  CatalogOfferingCardV2,
  CatalogOfferingTargetUrlCardV2,
} from "../offering_v2/components/more_to_explore";
import { Grid } from "./grid";
import { Heading } from "./heading";
import { PartnerConfig } from "./partner_config";
import { Spacer } from "./spacer";
import { UpsellCard } from "./template_catalog_base";
import { TalkWithUsPrompt } from "./template_offering_checkout_common";
import { Text } from "./text";
import { CatalogPartnerInfo } from "./catalog_partner_info";
import { OfferingMessageType } from "./api";
import { FilteredCatalog } from "@/offering_v2/components/filters/filtered_catalog";
import { FilterLocation } from "@/data/partner_offering";
import { useUTMParams } from "./utm";
import { useDisplayMerchantNameAndAddress } from "@/core/splitio";

interface TemplateCatalogFlexibleProps {
  partnerConfig: PartnerConfig;
  partnerCatalog: PartnerCatalog;
  locationAdress: string;
  partnerName?: string;
  catalogLocationFilter?: React.ReactNode;
  locations: FilterLocation[];
}

interface FlexibleGroupProps {
  catalogCategory: PartnerCatalogCategory;
  partnerCatalog: PartnerCatalog;
  partnerName?: string;
  locationAdress?: string;
}

const groupDataTestId = "flexible-template-catalog-group";

export const TemplateCatalogFlexible = (
  props: TemplateCatalogFlexibleProps
) => {
  const {
    partnerConfig,
    partnerCatalog,
    locationAdress,
    catalogLocationFilter,
    locations,
  } = props;
  const mq = useMediaQuery();
  const { locationID } = useQueryString();
  const {
    hubspotForm,
    favicon,
    localeOptions,
    showTalkWithUsPrompt = true,
    talkWithUsMessage,
    instagramLink,
    hubspotForms,
  } = partnerConfig;
  const {
    id,
    partner,
    partnerCatalogUpsell,
    partnerCatalogCategories: categories,
  } = partnerCatalog;
  const {
    id: partnerId,
    name: partnerName,
    homepageURL,
    logo: partnerLogo,
  } = partner;

  const { utmCampaign } = useUTMParams();
  const displayMerchantNameAndAddress = useDisplayMerchantNameAndAddress();

  const hasPartnerInfo =
    displayMerchantNameAndAddress &&
    utmCampaign !== "g-book-now" &&
    !!partnerName &&
    !!locationAdress;

  const hasDescriptionAndTitle =
    !!partnerCatalog.description && !!partnerCatalog.title;

  return (
    <>
      <Container paddingHorizontal={12}>
        <Helmet>
          <title>{partnerName} Catalog Page</title>
          <link href={favicon} rel="shortcut icon" type="image/x-icon" />
        </Helmet>
        <OfferingHeader
          homepageURL={homepageURL}
          logo={partnerLogo}
          offeringID={id}
          partnerID={partnerId}
          showTalkWithUsPrompt={showTalkWithUsPrompt}
          hubspotForm={hubspotForm}
          talkWithUsMessage={talkWithUsMessage}
          localeOptions={localeOptions}
          messageableType={OfferingMessageType.PartnerCatalog}
          hubspotForms={hubspotForms}
        />
      </Container>
      {hasPartnerInfo && (
        <Container paddingHorizontal={12}>
          <div className="container">
            <Spacer size={mq.device === "mobile" ? 16 : 48} />
            <CatalogPartnerInfo
              partnerName={partnerName}
              locationAdress={locationAdress}
            />
          </div>
        </Container>
      )}
      {hasDescriptionAndTitle && (
        <>
          <Spacer size={mq.device === "mobile" ? 16 : 48} />
          <Container paddingHorizontal={12}>
            <div className="container">
              <span className="fw-bold text-size-xl">
                {partnerCatalog.title}
              </span>
              <Spacer size={24} />

              <span className="text-size-md">
                <div
                  dangerouslySetInnerHTML={{
                    __html: partnerCatalog.description,
                  }}
                />
              </span>
            </div>
          </Container>
        </>
      )}
      {!!catalogLocationFilter && (
        <>
          {(hasPartnerInfo || hasDescriptionAndTitle) && (
            <Spacer size={mq.device === "mobile" ? 16 : 24} />
          )}
          {catalogLocationFilter}
        </>
      )}
      <Container paddingHorizontal={12}>
        <Spacer size={mq.device === "mobile" ? 16 : 48} />
        <div className="container">
          {locationID && !!catalogLocationFilter ? (
            <FilteredCatalog
              catalog={partnerCatalog}
              catalogSlug={id}
              locations={locations}
            />
          ) : (
            <Container>
              {categories.map((category) => (
                <FlexibleCategoryGroup
                  key={category.id}
                  partnerCatalog={partnerCatalog}
                  catalogCategory={category}
                />
              ))}
            </Container>
          )}
          <Spacer size={30} />
          {partnerCatalogUpsell && (
            <UpsellCard
              image={partnerCatalogUpsell.image}
              title={partnerCatalogUpsell.title}
              description={partnerCatalogUpsell.description}
              learnMoreURL={partnerCatalogUpsell.learnMoreURL}
              ctaLabel={partnerCatalogUpsell.ctaLabel}
              longDescription={partnerCatalogUpsell.longDescription}
            />
          )}
        </div>
        <Spacer size={24} />
        {/* TALK TO US MOBILE */}
        <div className="py-1 d-flex justify-content-center fw-bold d-block d-md-none">
          {showTalkWithUsPrompt ? (
            <TalkWithUsPrompt
              partnerID={partnerId}
              hubspotForm={hubspotForm}
              messageableID={id}
              messageableType={OfferingMessageType.PartnerCatalog}
              hubspotForms={hubspotForms}
            />
          ) : (
            talkWithUsMessage && `${talkWithUsMessage}`
          )}
        </div>
        <div className="container py-4">
          <FooterV2 instagramLink={instagramLink} />
        </div>
      </Container>
    </>
  );
};

export const FlexibleCategoryGroup = (props: FlexibleGroupProps) => {
  const { partnerCatalog, catalogCategory } = props;
  const { partner, slug: catalogSlug } = partnerCatalog;
  const { id: partnerId, partnerSlug } = partner;
  const { catalogOfferings } = catalogCategory;

  const mq = useMediaQuery();
  const isDesktop = mq.device === "desktop";

  if (isDesktop) {
    return (
      <div
        className="row"
        style={{ justifyContent: "center" }}
        data-testid={groupDataTestId}
      >
        <FlexibleTemplateGroupItems
          partnerID={partnerId}
          offerings={catalogOfferings}
          partnerSlug={partnerSlug}
          catalogSlug={catalogSlug}
          title={catalogCategory.title}
          description={catalogCategory.description}
        />
      </div>
    );
  }

  return (
    <div data-test-id={groupDataTestId}>
      <FlexibleTemplateGroupItems
        partnerID={partnerId}
        offerings={catalogOfferings}
        partnerSlug={partnerSlug}
        catalogSlug={catalogSlug}
        title={catalogCategory.title}
        description={catalogCategory.description}
      />
    </div>
  );
};

interface FlexibleTemplateGroupItemsProps {
  partnerSlug: string;
  catalogSlug?: string;
  partnerID: string;
  title: string;
  description: string;
  offerings?: CatalogOffering[];
}

const FlexibleTemplateGroupItems = (props: FlexibleTemplateGroupItemsProps) => {
  /**
   * For "flexible" template (aka "V3" catalog)
   * Render one category and its offering items
   */
  const { offerings, catalogSlug, title, description } = props;

  return (
    <Container className="mb-5">
      <div>
        <Heading size="md">{title}</Heading>
        <Spacer size={24} />

        <Text size="md">
          <div
            dangerouslySetInnerHTML={{
              __html: description,
            }}
            className="mb-5"
          />
        </Text>
      </div>
      <Grid itemsPerRow={{ md: 3, default: 1 }} spacing={32}>
        {offerings?.map((o, index) => {
          const {
            targetUrl,
            title,
            image,
            description,
            slug,
            comingSoon,
            membership,
            ctaLabel,
          } = o;

          return (
            <div
              key={o.id}
              data-testid="flexible-template-catalog-offering-item"
              className="h-100"
            >
              <Container key={title} paddingBottom={32} className="h-100">
                <Suspense
                  fallback={
                    <Container minHeight={240} center>
                      <PulseLoader />
                    </Container>
                  }
                >
                  {!targetUrl ? (
                    <CatalogOfferingCardV2
                      key={index}
                      image={image}
                      title={title}
                      description={description}
                      offeringSlug={slug}
                      catalogSlug={catalogSlug}
                      comingSoon={comingSoon}
                      membership={membership}
                      ctaLabel={ctaLabel}
                    />
                  ) : (
                    <CatalogOfferingTargetUrlCardV2
                      key={index}
                      image={image}
                      title={title}
                      description={description}
                      comingSoon={comingSoon}
                      targetUrl={targetUrl}
                      membership={membership}
                      ctaLabel={ctaLabel}
                    />
                  )}
                </Suspense>
              </Container>
            </div>
          );
        })}
      </Grid>
    </Container>
  );
};
